/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { SetStateAction } from "react";

import { Editor } from "slate";

import { SlashMenuOption } from "src/components/SlashMenu/slashMenuOptions";
import { EnhancedEditor, hasCommandModifier } from "src/components/ViennaEditor/lib/helpers";

import { Hotkeys } from "./types";

const HOTKEYS: Hotkeys = {
  b: "bold",
  i: "italic",
  u: "underline",
};
export const richTextHotkeys = (editor: Editor, e: React.KeyboardEvent): void => {
  if (hasCommandModifier(e)) {
    // if the key corresponds to a key command
    if (HOTKEYS[e.key as keyof Hotkeys] !== undefined) {
      e.preventDefault(); // don't insert content into editor
      e.stopPropagation();
      const format = HOTKEYS[e.key as keyof Hotkeys]; // grab the format from the hotkeys obj
      EnhancedEditor.toggleMark(editor, format); // toggle format that corresponds to the hotkey
    }
  }
};

interface Args {
  e: React.KeyboardEvent;
  inputType: "keyboard" | "mouse";
  setInputType: React.Dispatch<SetStateAction<"keyboard" | "mouse">>;
  selectedIndex: number;
  setSelectedIndex: React.Dispatch<SetStateAction<number>>;
  close: () => void;
  displayedOptions: SlashMenuOption[];
  selectOption: () => void;
}
export const handleSlashMenuKeydown = ({
  e,
  setInputType,
  selectedIndex,
  setSelectedIndex,
  close,
  displayedOptions,
  selectOption,
}: Args): { handled: boolean } => {
  switch (e.key) {
    case "ArrowDown":
      e.preventDefault();
      setInputType("keyboard");
      // increment selected index; loop back to top if going past the last option
      setSelectedIndex(selectedIndex + 1 > displayedOptions.length - 1 ? 0 : selectedIndex + 1);
      break;
    case "ArrowUp":
      e.preventDefault();
      setInputType("keyboard");
      // decrement selected index; loop back to bottom if going past the first option
      setSelectedIndex(selectedIndex - 1 < 0 ? displayedOptions.length - 1 : selectedIndex - 1);
      break;
    case "Enter":
      e.preventDefault(); // prevent enter from inserting newline into editor
      selectOption();
      return { handled: true };
    case "Escape":
      close(); // close self on escape
      return { handled: true };
    default:
      break;
  }
  return { handled: false };
};
